/**
 * The words and badges the approval screens agree on — §7.6.
 *
 * One thing in this file carries the whole feature: **required and advisory are
 * not two shades of the same badge.** §7.6 makes `requirement` a column so that
 * "legal should weigh in but isn't a gate" needs no second mechanism, and a
 * screen that renders both reviewers the same way has kept the table and thrown
 * the feature away. So the two badges differ in tone, in wording, and in the
 * sentence they carry: one says publishing waits, the other says it does not.
 *
 * The other rule here is what this file does **not** do. The API sends both an
 * `explanation` — a sentence written to be read by a person, and the same
 * sentence publish answers its 409 with — and a `blockedBy` enum. The sentence
 * is always rendered verbatim; the enum only ever becomes a short heading above
 * it. Writing a second explanation client-side would produce a screen that
 * disagrees with the refusal it is warning about, which is precisely the
 * credibility a confirmation runs on.
 */

import type {
  ApprovalBlock,
  ApprovalStatus,
  ReviewerDecision,
  ReviewerRequirement,
} from '../../api/types.ts';
import { Badge, type BadgeTone } from '../../components/ui/badge.tsx';

const REQUIREMENT_LABELS: Record<ReviewerRequirement, string> = {
  required: 'Required',
  advisory: 'Advisory',
};

const REQUIREMENT_MEANINGS: Record<ReviewerRequirement, string> = {
  required: 'Publishing waits on this reviewer (§7.6).',
  advisory:
    'Asked to weigh in, and never a gate — publishing does not wait on this reviewer (§7.6).',
};

export function requirementLabel(requirement: ReviewerRequirement): string {
  return REQUIREMENT_LABELS[requirement];
}

export function requirementMeaning(requirement: ReviewerRequirement): string {
  return REQUIREMENT_MEANINGS[requirement];
}

/**
 * Required is a warning tone and advisory is an outline, which is the same
 * grammar the rest of the shell uses for "this blocks something" against "this
 * is context". Neither is an error: an outstanding required reviewer is the
 * system working.
 */
export function RequirementBadge({ requirement }: { requirement: ReviewerRequirement }) {
  return (
    <Badge tone={requirement === 'required' ? 'warning' : 'outline'}>
      <span title={REQUIREMENT_MEANINGS[requirement]}>{REQUIREMENT_LABELS[requirement]}</span>
    </Badge>
  );
}

/**
 * A reviewer's decision, including the absence of one.
 *
 * Null is rendered rather than skipped. "Waiting" is the state an advisory
 * reviewer is allowed to stay in forever and the state a required reviewer
 * blocks from, so a blank cell would be hiding the only value that means
 * anything is outstanding.
 */
export function DecisionBadge({ decision }: { decision: ReviewerDecision | null }) {
  if (decision === null) {
    return (
      <Badge tone="gray">
        <span title="No decision recorded yet.">Waiting</span>
      </Badge>
    );
  }
  return (
    <Badge tone={decision === 'approved' ? 'success' : 'error'}>
      {decision === 'approved' ? 'Approved' : 'Rejected'}
    </Badge>
  );
}

const STATUS_LABELS: Record<ApprovalStatus, string> = {
  requested: 'Requested',
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
  expired: 'Expired',
};

/**
 * §7.6 gives `requested → pending` no cause, and the API picks the reading that
 * makes the edge worth having: `requested` means nobody has looked yet,
 * `pending` means somebody has. "Has anyone picked this up" is a question owners
 * actually ask, so the labels say it.
 */
const STATUS_MEANINGS: Record<ApprovalStatus, string> = {
  requested: 'Open, and nobody has decided yet.',
  pending: 'Open — somebody has decided, but not every required reviewer.',
  approved: 'Every required reviewer approved. Publishing is what promotes the draft (§7.4).',
  rejected:
    'A required reviewer rejected it. The record is immutable — supersede it with a new request.',
  expired: 'Withdrawn without a decision (§7.6), so a new request can be opened.',
};

const STATUS_TONES: Record<ApprovalStatus, BadgeTone> = {
  requested: 'outline',
  pending: 'brand',
  approved: 'success',
  rejected: 'error',
  expired: 'gray',
};

export function approvalStatusLabel(status: ApprovalStatus): string {
  return STATUS_LABELS[status] ?? status;
}

export function approvalStatusMeaning(status: ApprovalStatus): string {
  return STATUS_MEANINGS[status] ?? '';
}

export function ApprovalStatusBadge({ status }: { status: ApprovalStatus }) {
  return (
    <Badge tone={STATUS_TONES[status]}>
      <span title={STATUS_MEANINGS[status]}>{STATUS_LABELS[status]}</span>
    </Badge>
  );
}

/**
 * A heading for the gate, never a replacement for its sentence.
 *
 * Each value is a different fix — which is why §7.6's gate reports an enum
 * beside the prose rather than a boolean — and a person scanning a card needs
 * three words before they read forty. The forty are the server's.
 */
const BLOCK_HEADLINES: Record<ApprovalBlock, string> = {
  never_requested: 'Never sent for review',
  awaiting_required: 'Waiting on required reviewers',
  no_required_reviewers: 'No required reviewer',
  rejected: 'Rejected',
  expired: 'Withdrawn without a decision',
  draft_changed: 'Edited after it was approved',
};

export function blockHeadline(blockedBy: ApprovalBlock | null): string {
  return blockedBy === null ? 'Cleared to publish' : BLOCK_HEADLINES[blockedBy];
}
